import {
  Box,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material";
import React from "react";

import { CHAT_TOGGLE, TEXT } from "../../../common/colors";
import { useFilters } from "../../../common/hooks/useFilters";
import { Artifact, ArtifactType } from "../../../types/artifact";
import { AnswerMode, useChatStore } from "../stores/chatStore";
import { ChatActions, ChatState, DATASET_PRESET_TYPES, DatasetPreset, RowSelectionMode } from "../stores/chatTypes";
import { buildInteractionFilter } from "../utils/filters";

const CUSTOM_PRESET = "custom" as const;
const SUMMARIES_LENGTH = 1;
const TRANSCRIPTS_LENGTH = 2;
const NO_INTERACTIONS = 0;

const deriveDatasetPreset = (types: ArtifactType[]): DatasetPreset | null => {
  if (types.length === SUMMARIES_LENGTH && types[0] === ArtifactType.Summary) {
    return "summaries";
  }
  if (
    types.length === TRANSCRIPTS_LENGTH
    && types.includes(ArtifactType.Summary)
    && types.includes(ArtifactType.Transcript)
  ) {
    return "transcripts";
  }
  return null;
};

const createToggleGroupSx = (color: string): object => ({
  ["& .MuiToggleButton-root"]: {
    textTransform: "none",
    fontSize: "0.8rem",
    ["&.Mui-selected"]: {
      bgcolor: color,
      color: TEXT.white,
      ["&:hover"]: { bgcolor: color }
    }
  }
});

const styles = {
  container: {
    px: 2,
    pt: 1.5,
    pb: 0.5,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    bgcolor: TEXT.white,
    display: "flex",
    alignItems: "center",
    gap: 1.5
  },
  divider: {
    alignSelf: "center",
    height: 24,
    mx: 0.5
  },
  customPresetButton: {
    ["&.Mui-selected.Mui-disabled"]: {
      bgcolor: CHAT_TOGGLE.dataset,
      color: TEXT.white,
      opacity: 1
    }
  }
} as const;

interface UserSettingsPanelProps {
  selectedRows: Artifact[];
  loading: boolean;
  rowSelectionMode: RowSelectionMode;
}

interface RefreshContextParams {
  selectedRows: Artifact[];
  filters: ReturnType<typeof useFilters>["filters"];
  rowSelectionMode: RowSelectionMode;
  loadContext: (ChatState & ChatActions)["loadContext"];
}

const buildRefreshContext = (params: RefreshContextParams) =>
  (artifactTypes: ArtifactType[]): void => {
    const { selectedRows, filters, rowSelectionMode, loadContext } = params;
    const interactionIds = selectedRows
      .filter((row) => row.interaction_id)
      .map((row) => row.interaction_id);

    if (interactionIds.length > NO_INTERACTIONS) {
      const contextFilter = buildInteractionFilter(filters, rowSelectionMode, artifactTypes);
      loadContext(interactionIds, true, contextFilter);
    }
  };

interface DatasetToggleProps {
  datasetPreset: DatasetPreset | null;
  loading: boolean;
  onChange: (event: React.MouseEvent<HTMLElement>, newPreset: DatasetPreset | null) => void;
}

const DatasetToggle = ({ datasetPreset, loading, onChange }: DatasetToggleProps): React.ReactElement => (
  <ToggleButtonGroup
    value={datasetPreset ?? CUSTOM_PRESET}
    exclusive
    onChange={onChange}
    size="small"
    disabled={loading}
    sx={createToggleGroupSx(CHAT_TOGGLE.dataset)}
  >
    <ToggleButton value="summaries" data-testid="dataset-preset-summaries">Summaries</ToggleButton>
    <ToggleButton value="transcripts" data-testid="dataset-preset-transcripts">Transcripts</ToggleButton>
    {datasetPreset === null && (
      <ToggleButton
        value={CUSTOM_PRESET}
        sx={styles.customPresetButton}
        disabled
      >
        Custom
      </ToggleButton>
    )}
  </ToggleButtonGroup>
);

interface ModeToggleProps {
  answerMode: AnswerMode;
  loading: boolean;
  onChange: (event: React.MouseEvent<HTMLElement>, newMode: AnswerMode | null) => void;
}

const ModeToggle = ({ answerMode, loading, onChange }: ModeToggleProps): React.ReactElement => (
  <ToggleButtonGroup
    value={answerMode}
    exclusive
    onChange={onChange}
    size="small"
    disabled={loading}
    sx={createToggleGroupSx(CHAT_TOGGLE.answerMode)}
  >
    <ToggleButton value="quick" data-testid="answer-mode-explore">
      <Tooltip title="Surface patterns and themes quickly before committing to a full analysis" arrow>
        <span>Explore</span>
      </Tooltip>
    </ToggleButton>
    <ToggleButton value="deep" data-testid="answer-mode-deep">
      <Tooltip title="Build a thorough, structured answer with evidence and completeness prioritised" arrow>
        <span>Deeper Look</span>
      </Tooltip>
    </ToggleButton>
    <ToggleButton value="none" data-testid="answer-mode-none">
      <Tooltip title="Question is sent exactly as typed — no prompt guidance or framing applied" arrow>
        <span>None</span>
      </Tooltip>
    </ToggleButton>
  </ToggleButtonGroup>
);

interface ChangeHandlers {
  handleDatasetChange: (event: React.MouseEvent<HTMLElement>, newPreset: DatasetPreset | null) => void;
  handleModeChange: (event: React.MouseEvent<HTMLElement>, newMode: AnswerMode | null) => void;
}

interface BuildHandlersParams {
  setDatasetPreset: (ChatState & ChatActions)["setDatasetPreset"];
  setAnswerMode: (ChatState & ChatActions)["setAnswerMode"];
  refreshContext: (artifactTypes: ArtifactType[]) => void;
}

const buildChangeHandlers = ({ setDatasetPreset, setAnswerMode, refreshContext }: BuildHandlersParams): ChangeHandlers => ({
  handleDatasetChange: (_event, newPreset) => {
    if (newPreset === null) {
      return;
    }
    setDatasetPreset(newPreset);
    refreshContext(DATASET_PRESET_TYPES[newPreset]);
  },
  handleModeChange: (_event, newMode) => {
    if (newMode === null) {
      return;
    }
    setAnswerMode(newMode);
  }
});

const UserSettingsPanel = ({
  selectedRows,
  loading,
  rowSelectionMode
}: UserSettingsPanelProps): React.ReactElement => {
  const {
    includedArtifactTypes,
    setDatasetPreset,
    answerMode,
    setAnswerMode,
    loadContext
  } = useChatStore();
  const { filters } = useFilters();

  const datasetPreset = deriveDatasetPreset(includedArtifactTypes);
  const refreshContext = buildRefreshContext({ selectedRows, filters, rowSelectionMode, loadContext });
  const { handleDatasetChange, handleModeChange } = buildChangeHandlers({
    setDatasetPreset, setAnswerMode, refreshContext
  });

  return (
    <Box sx={styles.container}>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
        Analyse using:
      </Typography>
      <DatasetToggle datasetPreset={datasetPreset} loading={loading} onChange={handleDatasetChange} />
      <Divider orientation="vertical" flexItem sx={styles.divider} />
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
        Mode:
      </Typography>
      <ModeToggle answerMode={answerMode} loading={loading} onChange={handleModeChange} />
    </Box>
  );
};

export default UserSettingsPanel;
